import React, { useCallback, useContext, useEffect, useState } from 'react';

import { Input, InputProps } from '@nerdwallet/currency/components/Input';
import { Select } from '@nerdwallet/currency/components/Select';
import { InputDate } from '@nerdwallet/currency/components/InputDate';

import { FormContext } from './types';
import { InputDateProps } from '@nerdwallet/currency/dist/components/InputDate/InputDate';

type FormFieldProps<T> = T & {
  field: string;
  value?: string | number | readonly string[];
  Component?: React.ComponentType<T>;
};

/**
 * A form field used under a <Form /> component to automatically handle
 * propagation of the form field's data and error state. It takes a component
 * to use internally as a prop, so far this is most commonly an Input component.
 */
function FormField<T>({ field, Component, ...restProps }: FormFieldProps<T>) {
  const {
    formData,
    formErrors,
    setFormData,
    validators,
    register,
    unregister,
  } = useContext(FormContext);
  const [displayError, setDisplayError] = useState<string>();

  useEffect(() => {
    register(field);
    return () => unregister(field);
  }, [field, register, unregister]);

  const handleChange = useCallback(
    (e) => {
      const updatedValue = e.target.value;
      const updatedError = validators[field]?.(updatedValue);

      setFormData((existing) => ({
        ...existing,
        [field]: updatedValue,
      }));

      // Clear the display error immediately once the validation error is corrected.
      if (displayError && !updatedError) {
        setDisplayError(null);
      }
    },
    [validators, field, setFormData, displayError],
  );

  const handleBlur = useCallback(() => {
    const error = formErrors[field];
    if (error && displayError !== error) {
      setDisplayError(error);
    }
  }, [formErrors, field, displayError, setDisplayError]);

  return (
    <Component
      {...(restProps as T)}
      value={formData[field] ?? ''} // Disallow `undefined`, which are not valid for form inputs
      onChange={handleChange}
      onBlur={handleBlur}
      error={!!displayError}
      errorMessage={displayError}
    />
  );
}

type FormInputProps = {
  field: string;
} & InputProps;
export const FormInput = (props: FormInputProps) => (
  <FormField Component={Input} {...props} />
);

type FormSelectProps = {
  // <Select> is not written in TypeScript, so we're manually writing out its props
  block?: boolean;
  field: string;
  label: string;
  options: { disabled?: boolean; value: string | number; label: string }[];
};
export const FormSelect = (props: FormSelectProps) => (
  <FormField Component={Select} {...props} />
);

type FormInputDateProps = {
  field: string;
} & InputDateProps;
export const FormInputDate = (props: FormInputDateProps) => (
  <FormField Component={InputDate} {...props} />
);
